import React from 'react';
import Icon from '../../../components/AppIcon';

const InfrastructureHealthCard = ({ serviceHealth, alerts, performanceMetrics, loading }) => {
  const getStatusColor = (status) => {
    const colors = {
      operational: 'text-green-600 bg-green-50',
      degraded: 'text-yellow-600 bg-yellow-50',
      critical: 'text-red-600 bg-red-50',
      maintenance: 'text-blue-600 bg-blue-50'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-50';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      P0: 'text-red-600 bg-red-50',
      P1: 'text-orange-600 bg-orange-50',
      P2: 'text-yellow-600 bg-yellow-50',
      P3: 'text-blue-600 bg-blue-50'
    };
    return colors?.[priority] || 'text-gray-600 bg-gray-50';
  };

  const operationalCount = serviceHealth?.filter(s => s?.status === 'operational')?.length || 0;
  const degradedCount = serviceHealth?.filter(s => s?.status === 'degraded')?.length || 0;
  const criticalCount = serviceHealth?.filter(s => s?.status === 'critical')?.length || 0;

  const criticalAlerts = alerts?.filter(a => a?.priority === 'P0')?.length || 0;
  const highPriorityAlerts = alerts?.filter(a => a?.priority === 'P1')?.length || 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Activity" className="w-5 h-5 text-blue-600" />
          Infrastructure Health Overview
        </h2>
      </div>
      <div className="p-6 space-y-6">
        {/* Service Status Summary */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-3">Critical Service Status</div>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-900">Operational</span>
              </div>
              <span className="text-lg font-bold text-green-600">{operationalCount}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="AlertTriangle" className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-900">Degraded</span>
              </div>
              <span className="text-lg font-bold text-yellow-600">{degradedCount}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="XCircle" className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-900">Critical</span>
              </div>
              <span className="text-lg font-bold text-red-600">{criticalCount}</span>
            </div>
          </div>
        </div>

        {/* Database Performance */}
        {performanceMetrics?.database && (
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Icon name="Database" className="w-4 h-4 text-blue-600" />
              Database Performance
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Avg Query Time</span>
                <span className="font-medium text-gray-900">{performanceMetrics?.database?.queryPerformance?.avgQueryTime}ms</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Active Connections</span>
                <span className="font-medium text-gray-900">
                  {performanceMetrics?.database?.connections?.active}/{performanceMetrics?.database?.connections?.max}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Replication Status</span>
                <span className="font-medium text-green-600">{performanceMetrics?.database?.replication?.status}</span>
              </div>
            </div>
          </div>
        )}

        {/* API Gateway Metrics */}
        {performanceMetrics?.api && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-3">API Gateway Health</div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Requests/min</span>
                <span className="font-medium text-gray-900">{performanceMetrics?.api?.requestsPerMinute?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Avg Response Time</span>
                <span className="font-medium text-gray-900">{performanceMetrics?.api?.avgResponseTime}ms</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Error Rate</span>
                <span className={`font-medium ${
                  performanceMetrics?.api?.errorRate < 1 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {performanceMetrics?.api?.errorRate}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Network Connectivity */}
        {performanceMetrics?.network && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-3">Network Connectivity</div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Latency</span>
                <span className="font-medium text-gray-900">{performanceMetrics?.network?.latency}ms</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Packet Loss</span>
                <span className="font-medium text-green-600">{performanceMetrics?.network?.packetLoss}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Quality</span>
                <span className="font-medium text-green-600 capitalize">{performanceMetrics?.network?.connectionQuality}</span>
              </div>
            </div>
          </div>
        )}

        {/* Alert Priority Breakdown */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-3">Active Alerts by Priority</div>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-red-50 rounded">
              <span className="text-xs font-medium text-red-900">P0: System Down</span>
              <span className="text-sm font-bold text-red-600">{criticalAlerts}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
              <span className="text-xs font-medium text-orange-900">P1: Critical Degradation</span>
              <span className="text-sm font-bold text-orange-600">{highPriorityAlerts}</span>
            </div>
          </div>
        </div>

        {/* Real-time Status */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span>Real-time monitoring active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfrastructureHealthCard;
